import React, { useEffect, useRef, useState } from "react";
import { api, mediaSrc } from "../api";
import { useApp } from "../context/AppContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { Clock, Heart, Sticker as StickerIcon, Plus, Loader2, Trash2, Info, Camera, Video } from "lucide-react";
import { StickerStudio } from "./StickerStudio";

const readAsDataUrl = (file) => new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file); });

/** Save a static (PNG base64) sticker to "Mine". */
export const saveStaticSticker = (b64) =>
  api.post("/stickers/import", { images: [{ data_base64: b64, content_type: "image/png", filename: "photo-sticker.png" }] });

/** GIF / short video (+ crop + caption) → animated WebP rendered server-side, saved to "Mine". */
export const saveAnimatedSticker = (fd) =>
  api.post("/stickers/create", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 180000 });

export const stickerErrorKey = (e) => (e?.response?.status === 413 ? "sticker_too_large" : "sticker_render_failed");

/** Fetch a chat media item as a File so the Sticker Studio can crop/caption it. */
export async function mediaToFile(media) {
  const r = await fetch(mediaSrc(media));
  if (!r.ok) throw new Error(`http ${r.status}`);
  const blob = await r.blob();
  const type = blob.type && blob.type !== "application/octet-stream" ? blob.type : (media.content_type || (media.kind === "video" ? "video/mp4" : "image/jpeg"));
  return new File([blob], media.filename || (media.kind === "video" ? "clip.mp4" : "photo.jpg"), { type });
}

/** Animated WebP/GIF stickers are kept byte-for-byte (so the animation survives); static images are resized to 512 PNG. */
export async function fileToStickerPayload(file) {
  const animatedType = /^image\/(webp|gif)$/i.test(file.type) || /\.(webp|gif)$/i.test(file.name);
  if (animatedType && file.size <= 1.4 * 1024 * 1024) {
    return { data_base64: await readAsDataUrl(file), content_type: file.type || (/\.gif$/i.test(file.name) ? "image/gif" : "image/webp"), filename: file.name, animated: true };
  }
  return { data_base64: await fileToStickerDataUrl(file), content_type: "image/png", filename: file.name, animated: false };
}

/** Resize any image file (png/webp/jpg) to a 512x512 transparent-safe PNG data URL. */
export async function fileToStickerDataUrl(file) {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = url; });
    const c = document.createElement("canvas");
    c.width = 512; c.height = 512;
    const ctx = c.getContext("2d");
    const s = Math.min(512 / img.width, 512 / img.height);
    const w = img.width * s, h = img.height * s;
    ctx.drawImage(img, (512 - w) / 2, (512 - h) / 2, w, h);
    return c.toDataURL("image/png");
  } finally { URL.revokeObjectURL(url); }
}

export function StickerTray({ open, onClose, onSend }) {
  const { t, lang } = useApp();
  const [packs, setPacks] = useState([]);
  const [mine, setMine] = useState({ saved: [], recent: [] });
  const [tab, setTab] = useState("recent");
  const [busy, setBusy] = useState(false);
  const [manage, setManage] = useState(false);
  const fileRef = useRef(null);
  const photoRef = useRef(null);
  const videoRef = useRef(null);
  const [cropFile, setCropFile] = useState(null);

  const load = () => {
    api.get(`/stickers/packs?lang=${lang || "en"}`).then((r) => setPacks(r.data || [])).catch(() => {});
    api.get("/stickers/mine", { params: { lang } }).then((r) => setMine(r.data || { saved: [], recent: [] })).catch(() => {});
  };
  useEffect(() => { if (open) { load(); setManage(false); } }, [open]);
  useEffect(() => {
    if (!open) return;
    if (tab === "recent" && !mine.recent.length) setTab(mine.saved.length ? "mine" : (packs[0]?.id || "mine"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, packs.length, mine.recent.length, mine.saved.length]);

  const pick = async (st) => {
    if (manage) return;
    onSend(st);
    onClose();
    api.post("/stickers/recent", { media_id: st.id }).catch(() => {});
  };

  const importFiles = async (files) => {
    const list = [...files].filter((f) => /^image\//.test(f.type) || /\.(webp|png|gif)$/i.test(f.name));
    if (!list.length) return;
    setBusy(true);
    try {
      const images = [];
      for (const f of list.slice(0, 60)) images.push(await fileToStickerPayload(f));
      const r = await api.post("/stickers/import", { images });
      toast.success(t("stickers_imported").replace("{n}", r.data.created));
      load();
      setTab("mine");
    } catch (e) { toast.error(t("something_wrong")); } finally { setBusy(false); }
  };

  const createFromPhoto = async (b64) => {
    setBusy(true);
    try {
      await saveStaticSticker(b64);
      setCropFile(null);
      toast.success(t("sticker_saved"));
      load();
      setTab("mine");
    } catch (e) { toast.error(t("something_wrong")); } finally { setBusy(false); }
  };

  // GIF / short video (+ crop + caption) → animated WebP rendered server-side
  const createAnimated = async (fd) => {
    setBusy(true);
    try {
      await saveAnimatedSticker(fd);
      setCropFile(null);
      toast.success(t("sticker_saved"));
      load();
      setTab("mine");
    } catch (e) {
      toast.error(t(stickerErrorKey(e)));
    } finally { setBusy(false); }
  };

  const remove = async (st) => {
    const sid = st.saved_id || st.id; // localized variant keeps the id the user actually saved
    await api.delete(`/stickers/mine/${sid}`).catch(() => {});
    setMine((m) => ({ ...m, saved: m.saved.filter((x) => (x.saved_id || x.id) !== sid) }));
  };

  const list = tab === "recent" ? mine.recent : tab === "mine" ? mine.saved : (packs.find((p) => p.id === tab)?.stickers || []);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md p-0 overflow-hidden flex flex-col max-h-[80dvh]" data-testid="sticker-tray" onOpenAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader className="px-4 pt-4 pb-2 shrink-0">
          <DialogTitle className="font-display text-base flex items-center gap-2"><StickerIcon className="h-4 w-4" /> {t("stickers")}</DialogTitle>
        </DialogHeader>
        <div className="flex items-center gap-1 px-3 pb-2 overflow-x-auto shrink-0 [scrollbar-width:none]" data-testid="sticker-tabs">
          <TabBtn active={tab === "recent"} onClick={() => setTab("recent")} testid="sticker-tab-recent"><Clock className="h-4 w-4" /></TabBtn>
          <TabBtn active={tab === "mine"} onClick={() => setTab("mine")} testid="sticker-tab-mine"><Heart className="h-4 w-4" /></TabBtn>
          {packs.map((p) => (
            <TabBtn key={p.id} active={tab === p.id} onClick={() => setTab(p.id)} testid={`sticker-tab-${p.id}`}>
              {p.stickers[0] ? <img src={mediaSrc(p.stickers[0])} alt="" className="h-6 w-6 object-contain" /> : p.title}
            </TabBtn>
          ))}
          <div className="flex-1" />
          <Button size="sm" variant="outline" className="h-8 w-8 p-0 shrink-0" onClick={() => photoRef.current?.click()} disabled={busy} title={t("sticker_from_photo")} aria-label={t("sticker_from_photo")} data-testid="sticker-from-photo-btn">
            <Camera className="h-4 w-4" />
          </Button>
          <input ref={photoRef} type="file" accept="image/*,video/*,.gif,.mp4,.mov" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) setCropFile(f); e.target.value = ""; }} data-testid="sticker-photo-input" />
          <Button size="sm" variant="outline" className="h-8 w-8 p-0 shrink-0" onClick={() => videoRef.current?.click()} disabled={busy} title={t("record_video_sticker")} aria-label={t("record_video_sticker")} data-testid="sticker-record-video-btn">
            <Video className="h-4 w-4" />
          </Button>
          <input ref={videoRef} type="file" accept="video/*" capture="user" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) setCropFile(f); e.target.value = ""; }} data-testid="sticker-video-input" />
          <Button size="sm" variant="outline" className="h-8 shrink-0" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="sticker-import-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />} {t("add_sticker")}
          </Button>
          <input ref={fileRef} type="file" accept="image/*,.webp,.gif" multiple className="hidden" onChange={(e) => { importFiles(e.target.files); e.target.value = ""; }} data-testid="sticker-import-input" />
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto px-3 pb-3">
          {tab === "mine" && (
            <div className="flex items-center justify-between text-[11px] text-muted-foreground mb-2">
              <span className="flex items-center gap-1"><Info className="h-3 w-3" /> {t("sticker_import_hint")}</span>
              {mine.saved.length > 0 && (
                <button type="button" className="underline shrink-0 ml-2" onClick={() => setManage((m) => !m)} data-testid="sticker-manage-toggle">{manage ? t("done") : t("edit")}</button>
              )}
            </div>
          )}
          {list.length === 0 && (
            <div className="text-center text-sm text-muted-foreground py-10">{tab === "recent" ? t("no_recent_stickers") : tab === "mine" ? t("no_saved_stickers") : "—"}</div>
          )}
          <div className="grid grid-cols-4 gap-2">
            {list.map((st) => (
              <button key={st.id} type="button" onClick={() => pick(st)} className="relative aspect-square rounded-xl hover:bg-muted active:scale-95 transition-transform p-1" data-testid={`sticker-${st.id}`}>
                <img src={mediaSrc(st)} alt="" loading="lazy" className="h-full w-full object-contain" />
                {manage && tab === "mine" && (
                  <span role="button" onClick={(e) => { e.stopPropagation(); remove(st); }} className="absolute -top-1 -right-1 h-6 w-6 rounded-full bg-destructive text-white flex items-center justify-center shadow" data-testid={`sticker-remove-${st.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </DialogContent>
      <StickerStudio open={!!cropFile} file={cropFile} onClose={() => setCropFile(null)} onCreateStatic={createFromPhoto} onCreateAnimated={createAnimated} />
    </Dialog>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`h-9 min-w-9 px-2 rounded-lg flex items-center justify-center shrink-0 transition-colors ${active ? "bg-primary/15 text-primary" : "text-muted-foreground hover:bg-muted"}`}>
      {children}
    </button>
  );
}
